import { useEffect, useRef, useState } from 'react';
import { Room, RoomEvent, Track, type RemoteTrackPublication, type RemoteParticipant } from 'livekit-client';
import api from '@/utils/ky';

export enum LoadingState {
  INITIAL = 'initial',
  LOADING = 'loading',
  CONNECTING = 'connecting',
  ACTIVE = 'active',
  ENDING = 'ending',
  ERROR = 'error',
}

export enum CallState {
  IDLE = 'idle',
  CONNECTING = 'connecting',
  ACTIVE = 'active',
  ENDED = 'ended',
}

interface UseLivekitTestProps {
  sessionId: string;
  userId?: string;
}

export interface Transcript {
  source: 'ai' | 'user';
  message: string;
  timestamp: Date;
}

interface UseLivekitTestReturn {
  loadingState: LoadingState;
  callState: CallState;
  error: string | null;
  callDuration: number;
  isSpeaking: boolean;
  sessionData: any | null;
  transcript: Transcript[];
  handleStartCall: () => Promise<void>;
  handleEndCall: () => Promise<void>;
  formatTime: (seconds: number) => string;
}

export const useLivekitTest = ({
  sessionId,
  userId = 'test-user',
}: UseLivekitTestProps): UseLivekitTestReturn => {
  const [loadingState, setLoadingState] = useState<LoadingState>(LoadingState.INITIAL);
  const [callState, setCallState] = useState<CallState>(CallState.IDLE);
  const [error, setError] = useState<string | null>(null);
  const [callDuration, setCallDuration] = useState<number>(0);
  const [isSpeaking, setIsSpeaking] = useState<boolean>(false);
  const [sessionData, setSessionData] = useState<any | null>(null);
  const [transcript, setTranscript] = useState<Transcript[]>([]);

  const livekitRoomRef = useRef<Room | null>(null);
  const callTimerRef = useRef<NodeJS.Timeout | null>(null);
  const startTimeRef = useRef<number>(0);

  // Fetch session data on mount
  useEffect(() => {
    const fetchSession = async () => {
      try {
        setLoadingState(LoadingState.LOADING);
        console.log('Fetching scenario data for:', sessionId);

        const data = await api.get(`api/custom/scenarios/${sessionId}`).json();
        console.log('Scenario data loaded:', data);

        setSessionData(data);
        setLoadingState(LoadingState.INITIAL);
      } catch (err) {
        console.error('Error fetching scenario:', err);
        setError(err instanceof Error ? err.message : 'Failed to load scenario');
        setLoadingState(LoadingState.ERROR);
      }
    };

    if (sessionId) {
      fetchSession();
    }
  }, [sessionId]);

  // Call timer effect
  useEffect(() => {
    if (callState === CallState.ACTIVE) {
      callTimerRef.current = setInterval(() => {
        setCallDuration((prev) => prev + 1);
      }, 1000);
    } else {
      if (callTimerRef.current) {
        clearInterval(callTimerRef.current);
        callTimerRef.current = null;
      }
      setCallDuration(0);
    }

    return () => {
      if (callTimerRef.current) {
        clearInterval(callTimerRef.current);
      }
    };
  }, [callState]);

  const formatTime = (seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  const handleStartCall = async () => {
    try {
      setLoadingState(LoadingState.CONNECTING);
      setCallState(CallState.CONNECTING);
      setError(null);

      console.log('Starting LiveKit test call for session:', sessionId);

      // Request microphone access
      await navigator.mediaDevices.getUserMedia({ audio: true });

      // Initialize LiveKit room
      const room = new Room();
      livekitRoomRef.current = room;

      // Set up event listeners for audio tracks
      room.on(
        RoomEvent.TrackSubscribed,
        (
          track: RemoteTrackPublication['track'],
          _publication: RemoteTrackPublication,
          participant: RemoteParticipant,
        ) => {
          if (track?.kind === Track.Kind.Audio) {
            // Attach audio track to play agent's voice
            const audioElement = track.attach();
            document.body.appendChild(audioElement);
            audioElement.play();
            console.log('Agent audio track attached', participant.identity);
          }
        },
      );

      // Handle track unsubscribed
      room.on(
        RoomEvent.TrackUnsubscribed,
        (
          track: RemoteTrackPublication['track'],
          _publication: RemoteTrackPublication,
          participant: RemoteParticipant,
        ) => {
          if (track?.kind === Track.Kind.Audio) {
            track.detach().forEach((element) => element.remove());
            console.log('Agent audio track detached', participant.identity);
          }
        },
      );

      // Handle connection state changes
      room.on(RoomEvent.Connected, () => {
        console.log('Connected to LiveKit room');
        setCallState(CallState.ACTIVE);
        setLoadingState(LoadingState.ACTIVE);
        startTimeRef.current = Date.now();
      });

      room.on(RoomEvent.Disconnected, () => {
        console.log('Disconnected from LiveKit room');
      });

      room.on(RoomEvent.Reconnecting, () => {
        console.log('Reconnecting to LiveKit room...');
      });

      room.on(RoomEvent.Reconnected, () => {
        console.log('Reconnected to LiveKit room');
      });

      // Handle participant speaking state via attributes
      room.on(
        RoomEvent.ParticipantAttributesChanged,
        (changedAttributes: Record<string, string>, participant: RemoteParticipant) => {
          if (participant.identity.startsWith('agent-')) {
            const agentState = changedAttributes['lk.agent.state'];
            const nowSpeaking = agentState === 'speaking';
            console.log('🤖 Agent speaking state:', nowSpeaking);
            setIsSpeaking(nowSpeaking);
          }
        },
      );

      // Handle transcription from LiveKit agent
      room.on(
        RoomEvent.TranscriptionReceived,
        (segments: any[], participant?: RemoteParticipant) => {
          console.log('📝 Transcription received:', {
            segmentCount: segments.length,
            participantIdentity: participant?.identity,
            segments: segments.map((s) => ({
              id: s.id,
              text: s.text,
              final: s.final,
            })),
          });

          segments.forEach((segment) => {
            // Only process final segments to avoid word-by-word updates
            if (segment.final && segment.text.length > 0) {
              const isAgent = participant?.identity?.startsWith('agent-');

              console.log('📝 Final transcription:', {
                isAgent,
                text: segment.text,
                participantIdentity: participant?.identity,
              });

              setTranscript((prev) => [
                ...prev,
                {
                  source: isAgent ? 'ai' : 'user',
                  message: segment.text,
                  timestamp: new Date(),
                },
              ]);
            }
          });
        },
      );

      // Handle agent messages via data channel (fallback)
      room.on(
        RoomEvent.DataReceived,
        (payload: Uint8Array, _participant?: RemoteParticipant) => {
          try {
            const message = JSON.parse(new TextDecoder().decode(payload));
            console.log('📨 Data received:', message);

            // Add to transcript if not already handled by transcription
            if (message.text || message.message) {
              const text = message.text || message.message;
              setTranscript((prev) => {
                // Avoid duplicates - check if last message is the same
                const lastMsg = prev[prev.length - 1];
                if (lastMsg && lastMsg.message === text &&
                    lastMsg.source === (message.source || 'ai')) {
                  return prev;
                }

                return [
                  ...prev,
                  {
                    source: message.source || 'ai',
                    message: text,
                    timestamp: new Date(),
                  },
                ];
              });
            }
          } catch (error) {
            console.error('Error parsing data message:', error);
          }
        },
      );

      // Use session ID as room name
      const roomName = sessionId;

      // Get LiveKit token from backend
      const tokenResponse = await api
        .get('livekit/token', {
          searchParams: {
            username: userId,
            room: roomName,
            livekitAgentName: import.meta.env.VITE_LIVEKIT_TEST_AGENT_NAME || 'LIVEKIT-DEMO-DEV-TEST-AGENT',
          },
        })
        .json<{ token: string }>();

      const livekitUrl = import.meta.env.VITE_LIVEKIT_URL;

      if (!livekitUrl) {
        throw new Error('VITE_LIVEKIT_URL environment variable is not set');
      }

      console.log('Connecting to LiveKit:', { url: livekitUrl, room: roomName });

      // Connect to LiveKit room
      await room.connect(livekitUrl, tokenResponse.token);
      console.log('Successfully connected to LiveKit room');

      // Enable microphone
      await room.localParticipant.setMicrophoneEnabled(true);
      console.log('Microphone enabled');

    } catch (err) {
      console.error('Error starting call:', err);
      setError(err instanceof Error ? err.message : 'Failed to start call');
      setLoadingState(LoadingState.ERROR);
      setCallState(CallState.IDLE);
    }
  };

  const handleEndCall = async () => {
    try {
      setLoadingState(LoadingState.ENDING);

      // Disconnect LiveKit session
      if (livekitRoomRef.current) {
        // Disable microphone before disconnecting
        await livekitRoomRef.current.localParticipant.setMicrophoneEnabled(false);
        // Disconnect from room
        await livekitRoomRef.current.disconnect();
        console.log('Disconnected from LiveKit room');
        livekitRoomRef.current = null;
      }

      // End call
      setCallState(CallState.ENDED);
      setLoadingState(LoadingState.INITIAL);

      console.log('Call ended successfully');
    } catch (err) {
      console.error('Error ending call:', err);
      setError(err instanceof Error ? err.message : 'Failed to end call');
      setCallState(CallState.ENDED);
      setLoadingState(LoadingState.INITIAL);
    }
  };

  return {
    loadingState,
    callState,
    error,
    callDuration,
    isSpeaking,
    sessionData,
    transcript,
    handleStartCall,
    handleEndCall,
    formatTime,
  };
};
